import { captureEvent } from "@Light/app/analytics";
import { BorderedContent } from "@Light/components/page/BorderedContent";
import { Button } from "@Light/components/page/Button";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import { Spinner } from "@Light/components/system/Spinner";
import { useScaffold } from "@Light/scaffold";
import { useLight } from "@Light/services/light";
import { useSearchParams } from "@Light/utils/context";
import { CheckIcon, MinusIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import React from "react";
import { ChecklistDisplayItem, createChecklistDisplayItems } from "./helpers";

export type BatteryConclusionProps = {
  onComplete?: (reason: string) => void;
};

function LoadingState() {
  const scaffold = useScaffold();
  const PageBody = scaffold.page.pageBody;

  return (
    <PageBody
      centered
      title="Syncing your battery"
      icon={<Spinner className="w-8 h-8" />}
    >
      <p className={clsx("text-base", scaffold.page.colors.text.tertiary)}>
        This may take a moment. We're connecting to your battery provider and
        discovering your device.
      </p>
    </PageBody>
  );
}

type ErrorStateProps = {
  onRetry: () => void;
  onCancel: () => void;
};

function ErrorState({ onRetry, onCancel }: ErrorStateProps) {
  const scaffold = useScaffold();
  const PageBody = scaffold.page.pageBody;

  return (
    <PageBody
      centered
      title="Unable to load battery information"
      footer={
        <ButtonFooter>
          <Button size="lg" className="w-full" onClick={onRetry}>
            Try again
          </Button>
          <Button
            size="lg"
            buttonType="outline"
            className="w-full"
            onClick={onCancel}
          >
            Complete setup later
          </Button>
        </ButtonFooter>
      }
    >
      <p className={clsx("text-base", scaffold.page.colors.text.tertiary)}>
        We couldn't find any battery devices. This might take a few minutes for
        your battery provider to sync. Please try again in a moment.
      </p>
    </PageBody>
  );
}

type ChecklistItemProps = {
  item: ChecklistDisplayItem;
};

function ChecklistItem({ item }: ChecklistItemProps) {
  const scaffold = useScaffold();

  return (
    <div className="flex items-center gap-3">
      <div className="shrink-0">
        {item.completed ? (
          <CheckIcon
            className={clsx("w-5 h-5", scaffold.page.colors.text.primary)}
          />
        ) : (
          <MinusIcon
            className={clsx("w-5 h-5", scaffold.page.colors.text.tertiary)}
          />
        )}
      </div>
      <div
        className={clsx(
          "text-sm",
          item.completed
            ? scaffold.page.colors.text.secondary
            : scaffold.page.colors.text.tertiary,
        )}
      >
        {item.label}
      </div>
    </div>
  );
}

type SettingsChecklistProps = {
  manufacturer: string;
  checklistItems: ChecklistDisplayItem[];
  isDiscovering: boolean;
  onRefresh: (e: React.MouseEvent) => void;
  onAllSet: () => void;
  onSetupLater: () => void;
};

function SettingsChecklist({
  manufacturer,
  checklistItems,
  isDiscovering,
  onRefresh,
  onAllSet,
  onSetupLater,
}: SettingsChecklistProps) {
  const scaffold = useScaffold();
  const PageBody = scaffold.page.pageBody;
  const Hyperlink = scaffold.page.hyperlink;

  const completedCount = checklistItems.filter((item) => item.completed).length;
  const totalCount = checklistItems.length;
  const allComplete = completedCount === totalCount;

  return (
    <PageBody
      centered
      title={`Open the ${manufacturer} app and set the following settings`}
      footer={
        <ButtonFooter>
          <Button
            className="w-full"
            disabled={!allComplete}
            onClick={onAllSet}
            data-testid="battery-all-set"
          >
            All set
          </Button>
          <Button
            buttonType="outline"
            className="w-full"
            onClick={onSetupLater}
          >
            Set up later
          </Button>
        </ButtonFooter>
      }
    >
      <BorderedContent
        className="mt-4"
        header={
          <div className="flex items-center justify-between">
            <span>
              {completedCount} of {totalCount} complete
            </span>
            <Hyperlink
              href="#"
              onClick={onRefresh}
              className={clsx(
                "text-xs font-normal",
                scaffold.page.colors.text.tertiary,
                isDiscovering && "opacity-50 pointer-events-none",
              )}
            >
              {isDiscovering ? "Refreshing" : "Refresh"}
            </Hyperlink>
          </div>
        }
      >
        <div className="flex flex-col gap-4 mt-4">
          {checklistItems.map((item) => (
            <ChecklistItem key={item.field} item={item} />
          ))}
        </div>
      </BorderedContent>

      <div className="flex flex-col gap-2 mt-12">
        <p
          className={clsx(
            "text-sm font-semibold",
            scaffold.page.colors.text.secondary,
          )}
        >
          What if I don't see the above settings in my app?
        </p>
        <p className={clsx("text-sm", scaffold.page.colors.text.secondary)}>
          Sometimes your battery installer needs to enable these settings on the
          battery hardware. Reach out to your installer to enable these
          settings.
        </p>
      </div>
    </PageBody>
  );
}

export function BatteryConclusion({ onComplete }: BatteryConclusionProps) {
  const [searchParams] = useSearchParams();
  const deviceUuid = searchParams.get("device_uuid");
  const batteryConnected = searchParams.get("battery_connected");
  const MAX_RETRIES = 10; // ~74 seconds total (0s + 2s + 4s + 8s + 6×10s)

  const [discoverCount, setDiscoverCount] = React.useState(0);
  const { useGetDeviceQuery, useDiscoverDevicesMutation, useListDevicesQuery } =
    useLight();

  const {
    data: batteryDevice,
    isLoading: isLoadingDevice,
    isError: isDeviceError,
  } = useGetDeviceQuery(deviceUuid || "", {
    skip: !deviceUuid,
  });

  const {
    data: devicesPage,
    isLoading: isLoadingDevices,
    refetch: refetchDevices,
    isError: isDevicesError,
  } = useListDevicesQuery(
    { device_type: "BATTERY" },
    { skip: !batteryConnected },
  );

  const device = batteryDevice || devicesPage?.data?.[0];
  const isLoading = isLoadingDevice || isLoadingDevices;
  const hasExceededRetries = discoverCount >= MAX_RETRIES && !device;
  // Show error only if both endpoints fail or max retries exceeded
  // one endpoint could succeed and the other fail, in which case we shouldn't show an error
  const isError = (isDevicesError && isDeviceError) || hasExceededRetries;

  const [discoverDevices, { isLoading: isDiscovering }] =
    useDiscoverDevicesMutation();

  // React compiler is enabled, should memoize for us.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const runDiscover = () => {
    setDiscoverCount((c) => c + 1);
    void discoverDevices().then(() => void refetchDevices());
  };

  React.useEffect(() => {
    if (batteryConnected !== "true") return;
    if (isDiscovering) return;
    if (discoverCount >= MAX_RETRIES) return;
    if (device) return;

    const delay =
      discoverCount === 0
        ? 0
        : Math.min(1000 * Math.pow(2, discoverCount), 10000);
    const timer = setTimeout(() => {
      runDiscover();
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [batteryConnected, device, discoverCount, isDiscovering, runDiscover]);

  const manufacturer = device?.battery_details.manufacturer || "your battery";
  const backendChecklist = device?.program?.compliance_checklist || [];
  const checklistItems = createChecklistDisplayItems(backendChecklist);

  const handleRefresh = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await discoverDevices().unwrap();
      await refetchDevices();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      // Ignore errors
    }
  };

  const handleAllSet = () => {
    captureEvent("device_connection_completed", {
      device_type: "battery",
      manufacturer,
    });
    if (onComplete) {
      onComplete("battery_connection_completed");
    }
  };

  const handleSetupLater = () => {
    captureEvent("device_connection_skipped", {
      device_type: "battery",
      manufacturer,
    });
    if (onComplete) {
      onComplete("battery_connection_skipped");
    }
  };

  const handleRetry = () => {
    setDiscoverCount(0);
    runDiscover();
  };

  const handleCancel = () => {
    if (onComplete) {
      onComplete("battery_connection_cancelled");
    }
  };

  if (device) {
    return (
      <SettingsChecklist
        manufacturer={manufacturer}
        checklistItems={checklistItems}
        isDiscovering={isDiscovering}
        onRefresh={handleRefresh}
        onAllSet={handleAllSet}
        onSetupLater={handleSetupLater}
      />
    );
  }

  if (isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (isError) {
    return <ErrorState onRetry={handleRetry} onCancel={handleCancel} />;
  }

  return <LoadingState />;
}
